import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { displayFileName } from '@/lib/utils';
import FieldRenderer from '@/components/FieldRenderer';
import OutputViewer, { getOutputKind } from '@/components/OutputViewer';
import { copysightRefreshKey, mergeCopySight, useExecutionCopySight } from '@/hooks/useExecutionCopySight';
import { useRealtimeWorkflows, type WorkflowDetail, type InputField, type ExecutionStatus } from '@/hooks/useRealtimeWorkflows';
import {
  ArrowLeft,
  Play,
  Loader2,
  CheckCircle,
  XCircle,
  Download,
  RotateCcw,
  Image,
  Video,
  ChevronDown,
  ChevronUp,
  FileText,
  EyeOff,
  Eye,
} from 'lucide-react';

type RunPhase = 'input' | 'running' | 'completed' | 'error';

function groupFieldsByNode(fields: InputField[]): { nodeId: string; label: string; fields: InputField[] }[] {
  const groups = new Map<string, { nodeId: string; label: string; fields: InputField[] }>();
  for (const field of fields.filter((f) => f.visible)) {
    if (!groups.has(field.nodeId)) {
      const nodeLabel = field.label.split(' — ')[0];
      groups.set(field.nodeId, {
        nodeId: field.nodeId,
        label: nodeLabel,
        fields: [],
      });
    }
    groups.get(field.nodeId)!.fields.push(field);
  }
  return [...groups.values()];
}

interface RunPageProps {
  // When provided, RunPage renders this workflow live instead of fetching it —
  // used by the manager's Test panel so config edits (show/hide, labels, allowed
  // values, order) appear immediately without a save/refetch.
  workflowOverride?: WorkflowDetail | null;
}

export default function RunPage({ workflowOverride }: RunPageProps = {}) {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const fromExecId = searchParams.get('from');
  const { executions, getWorkflow, executeWorkflow, cancelExecution, toggleHideOutput } = useRealtimeWorkflows();
  const isEmbedded = !!workflowOverride;

  const [workflow, setWorkflow] = useState<WorkflowDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<RunPhase>('input');
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState('');
  const [errorExpanded, setErrorExpanded] = useState(false);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [status, setStatus] = useState<ExecutionStatus>({ status: 'queued' });
  const [activePromptId, setActivePromptId] = useState<string | null>(null);
  const [prevFileNames, setPrevFileNames] = useState<Record<string, string>>({});
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [hidingOutputs, setHidingOutputs] = useState<Set<string>>(new Set());

  const handleReviewPdf = useCallback(async () => {
    const execRecord = executions.find((e) => e.id === activePromptId);
    if (!execRecord || pdfBusy) return;
    setPdfBusy(true);
    setPdfError('');
    try {
      const { downloadReviewPdf } = await import('@/lib/reviewDoc');
      await downloadReviewPdf(execRecord);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Failed to generate the review document');
    } finally {
      setPdfBusy(false);
    }
  }, [executions, activePromptId, pdfBusy]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getWorkflow(id);
      setWorkflow(data);
      const initial: Record<string, unknown> = {};
      for (const field of data.inputMask.filter((f) => f.visible)) {
        // Clamp numeric defaults to manager's constraints
        let value = field.defaultValue;
        if ((field.widget === 'slider' || field.widget === 'number') && typeof value === 'number') {
          if (field.min !== undefined) value = Math.max(value, field.min);
          if (field.max !== undefined) value = Math.min(value, field.max);
        }
        initial[field.id] = value;
      }

      if (fromExecId) {
        // Get the execution from current executions state at load time
        const prev = executions.find((e) => e.id === fromExecId);
        if (prev?.inputParams) {
          for (const [key, val] of Object.entries(prev.inputParams)) {
            if (key in initial) {
              initial[key] = val;
            }
          }
        }
        if (prev?.inputFileNames) {
          setPrevFileNames(prev.inputFileNames);
        }
      }

      setValues(initial);
    } catch {
      setError('Failed to load workflow');
    } finally {
      setLoading(false);
    }
  }, [id, fromExecId, getWorkflow]);

  useEffect(() => {
    // Embedded: the parent supplies the (live-edited) workflow; don't fetch.
    if (isEmbedded) return;
    load();
  }, [load, isEmbedded]);

  // Embedded: mirror the override as it's edited. Reflect visibility/label/order
  // /options changes live, seed defaults for newly-shown fields, and preserve
  // values the tester already typed for fields that still exist.
  useEffect(() => {
    if (!workflowOverride) return;
    setWorkflow(workflowOverride);
    setLoading(false);
    setValues((prev) => {
      const next: Record<string, unknown> = {};
      for (const field of workflowOverride.inputMask.filter((f) => f.visible)) {
        if (field.id in prev) {
          next[field.id] = prev[field.id];
        } else {
          // Clamp numeric defaults to manager's constraints
          let value = field.defaultValue;
          if ((field.widget === 'slider' || field.widget === 'number') && typeof value === 'number') {
            if (field.min !== undefined) value = Math.max(value, field.min);
            if (field.max !== undefined) value = Math.min(value, field.max);
          }
          next[field.id] = value;
        }
      }
      return next;
    });
  }, [workflowOverride]);

  // Real-time execution monitoring via Firestore listener
  useEffect(() => {
    if (!activePromptId) return;

    const currentExec = executions.find((e) => e.id === activePromptId);
    if (!currentExec) return;

    const newStatus: ExecutionStatus = {
      status: currentExec.status as ExecutionStatus['status'],
      workflowId: currentExec.workflowId,
      outputs: currentExec.outputs,
      error: currentExec.error,
    };

    setStatus(newStatus);

    if (currentExec.status === 'completed') {
      setPhase('completed');
    } else if (currentExec.status === 'error') {
      setPhase('error');
      setError(currentExec.error || 'Workflow execution failed');
      setErrorDetails(currentExec.errorDetails || '');
    }
  }, [activePromptId, executions]);

  const missingRequired = workflow
    ? workflow.inputMask
        .filter((f) => f.visible && f.required)
        .filter((f) => {
          const v = values[f.id];
          const isFileWidget = f.widget === 'file-image' || f.widget === 'file-video' || f.widget === 'file-audio';
          if (isFileWidget) {
            // Check if file has been uploaded (has storagePath)
            return !(v && typeof v === 'object' && 'storagePath' in v);
          }
          if (v === undefined || v === null || v === '') return true;
          return false;
        })
    : [];

  const handleSubmit = async () => {
    if (!id || !workflow) return;
    if (missingRequired.length > 0) {
      setError(`Please fill in: ${missingRequired.map((f) => f.label.split(' — ')[1] || f.inputName).join(', ')}`);
      return;
    }
    setPhase('running');
    setError(null);
    setStatus({ status: 'queued' });

    try {
      const params: Record<string, unknown> = {};
      const filePaths: Record<string, string> = {};
      for (const field of workflow.inputMask.filter((f) => f.visible)) {
        const val = values[field.id];
        // Check if this is an uploaded file with storagePath
        if (val && typeof val === 'object' && 'storagePath' in val) {
          filePaths[field.id] = (val as { storagePath: string }).storagePath;
        } else if (val !== undefined && val !== null) {
          params[field.id] = val;
        }
      }
      const result = await executeWorkflow(id, params, filePaths);
      if (result?.promptId) {
        setActivePromptId(result.promptId);
        // Real-time listener will handle updates automatically
      } else {
        setPhase('error');
        setError('No prompt ID returned');
      }
    } catch (err) {
      setPhase('error');
      setError(err instanceof Error ? err.message : 'Execution failed');
    }
  };

  const handleCancel = async () => {
    if (!activePromptId) return;
    try {
      await cancelExecution(activePromptId);
    } catch {
      // ignore cancel errors
    }
    setPhase('input');
    setError(null);
    setStatus({ status: 'queued' });
    setActivePromptId(null);
  };

  const handleReset = () => {
    setPhase('input');
    setError(null);
    setStatus({ status: 'queued' });
    setActivePromptId(null);
  };

  // Display/download names with the backend's anti-collision segment stripped,
  // so the user sees the name they intended. Stored filename/URL is unchanged.
  const downloadNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const output of status.outputs || []) {
      map[output.filename] = displayFileName(output.filename);
    }
    return map;
  }, [status.outputs]);

  const handleDownload = async (url: string, downloadName: string) => {
    // Fetch the signed bucket URL as a blob so it downloads directly under the
    // chosen name instead of navigating to the bucket's inline view.
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleToggleHideOutput = async (filename: string, hidden: boolean) => {
    if (!activePromptId) return;
    setHidingOutputs((prev) => new Set(prev).add(filename));
    try {
      await toggleHideOutput(activePromptId, filename, hidden);
    } catch (err) {
      console.error('Failed to toggle hide:', err);
    } finally {
      setHidingOutputs((prev) => {
        const next = new Set(prev);
        next.delete(filename);
        return next;
      });
    }
  };

  // Once the run completes, fetch it from the backend to get each output's
  // CopySight report (detections + boxes), which the Firestore document does not hold.
  const { reports: copysightReports, loading: copysightLoading } = useExecutionCopySight(
    activePromptId ?? undefined,
    status.status === 'completed',
    copysightRefreshKey(status.outputs),
  );
  const outputs = useMemo(() => mergeCopySight(status.outputs || [], copysightReports), [status.outputs, copysightReports]);

  const closeViewer = useCallback(() => setViewerIndex(null), []);
  const showPrev = useCallback(
    () => setViewerIndex((i) => (i === null ? i : (i - 1 + outputs.length) % outputs.length)),
    [outputs.length],
  );
  const showNext = useCallback(() => setViewerIndex((i) => (i === null ? i : (i + 1) % outputs.length)), [outputs.length]);

  // Keyboard navigation while the outputs viewer is open.
  useEffect(() => {
    if (viewerIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeViewer();
      else if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewerIndex, closeViewer, showPrev, showNext]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{error || 'Workflow not found'}</p>
        </div>
      </div>
    );
  }

  const inputGroups = groupFieldsByNode(workflow.inputMask);

  const allFields = inputGroups.flatMap((g) => g.fields).sort((a, b) => a.order - b.order);

  return (
    <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 overflow-auto">
      <div className="max-w-lg mx-auto px-6 py-6 pb-16">
        {/* Header — no back link when embedded in the manager's Test panel */}
        {!isEmbedded && (
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6 w-fit">
            <ArrowLeft className="h-3 w-3" />
            Back
          </Link>
        )}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{workflow.name}</h1>
          {(workflow as unknown as { description?: string }).description && (
            <p className="text-sm text-muted-foreground mt-2">{(workflow as unknown as { description?: string }).description}</p>
          )}
        </div>

        {/* Form */}
        <div
          className={
            'rounded-2xl border border-border/50 bg-card ' +
            'shadow-lg shadow-black/[0.03] dark:shadow-black/20 ' +
            'overflow-hidden ' +
            (phase !== 'input' ? 'opacity-50 pointer-events-none' : '')
          }
        >
          <div className="p-5 space-y-5">
            {allFields.map((field, i) => (
              <div key={field.id}>
                {i > 0 && <div className="h-px bg-gradient-to-r from-transparent via-border/50 to-transparent mb-5" />}
                <FieldRenderer
                  field={field}
                  value={values[field.id]}
                  onChange={(v) => setValues((prev) => ({ ...prev, [field.id]: v }))}
                  readOnly={phase !== 'input'}
                  previousFileName={prevFileNames[field.id]}
                />
              </div>
            ))}
          </div>

          {phase === 'input' && (
            <div className="px-5 pb-5 pt-2">
              <div className="h-px bg-gradient-to-r from-transparent via-border/50 to-transparent mb-4" />
              <Button
                onClick={handleSubmit}
                size="lg"
                className={
                  'w-full h-14 text-base font-semibold rounded-xl ' +
                  'bg-gradient-to-r from-violet-600 to-violet-700 ' +
                  'hover:from-violet-700 hover:to-violet-800 ' +
                  'shadow-lg shadow-violet-500/25 ' +
                  'transition-all hover:shadow-xl hover:shadow-violet-500/30 ' +
                  'hover:-translate-y-0.5'
                }
              >
                <Play className="h-5 w-5 mr-2.5" />
                Run Workflow
              </Button>
            </div>
          )}
        </div>

        {/* Status / Results */}
        {phase === 'running' && (
          <div className="mt-6">
            <Card className="border-violet-300/40 dark:border-violet-500/30">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <Loader2 className="h-10 w-10 animate-spin text-violet-500 mx-auto" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {status.status === 'queued' && 'Queued...'}
                      {status.status === 'running' && 'Processing...'}
                      {status.status === 'localizing' && 'Receiving assets...'}
                      {status.status !== 'queued' &&
                        status.status !== 'running' &&
                        status.status !== 'localizing' &&
                        'Working...'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {status.status === 'queued' && 'Waiting in the queue'}
                      {status.status === 'running' &&
                        'Your workflow is being processed by AICS Workflow Runner. This can take several minutes depending on workflow complexity.'}
                      {status.status === 'localizing' && 'Receiving output files'}
                    </p>
                  </div>
                  <div className="w-full bg-accent rounded-full h-1.5 overflow-hidden">
                    <div className="bg-violet-500 h-1.5 rounded-full animate-pulse w-2/3" />
                  </div>
                  <Button variant="outline" size="sm" onClick={handleCancel} className="mt-2">
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {phase === 'completed' && status.outputs && (
          <div className="mt-6">
            <Card className="border-green-300/40 dark:border-green-500/30">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Complete
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {status.outputs.map((output, i) => {
                  const kind = getOutputKind(output.filename);
                  const isImage = kind === 'image';
                  const isVideo = kind === 'video';
                  const isFile = !isImage && !isVideo;
                  const name = downloadNames[output.filename] || output.filename;
                  return (
                    <div
                      key={i}
                      onClick={() => setViewerIndex(i)}
                      className={
                        'group relative rounded-xl border border-border/60 overflow-hidden ' +
                        'cursor-pointer hover:border-violet-500/50 transition-colors'
                      }
                    >
                      {isImage && <img src={output.url} alt={name} className="w-full" />}
                      {isVideo && <video src={output.url} className="w-full" muted />}
                      {isFile && (
                        <div className="flex items-center justify-center py-6 bg-accent/20">
                          <Download className="h-8 w-8 text-muted-foreground/30" />
                        </div>
                      )}
                      <div className="flex items-center justify-between px-3 py-2 bg-accent/30">
                        <span className="text-xs text-muted-foreground truncate flex items-center gap-1">
                          {isImage && <Image className="h-3 w-3" />}
                          {isVideo && <Video className="h-3 w-3" />}
                          {isFile && <Download className="h-3 w-3" />}
                          {name}
                          {output.hidden && <span className="text-[10px] text-muted-foreground/60 italic">(hidden)</span>}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleHideOutput(output.filename, !output.hidden);
                            }}
                            disabled={hidingOutputs.has(output.filename)}
                            className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                            title={output.hidden ? 'Show in My Outputs' : 'Hide from My Outputs'}
                          >
                            {hidingOutputs.has(output.filename) ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : output.hidden ? (
                              <Eye className="h-3.5 w-3.5" />
                            ) : (
                              <EyeOff className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(output.url, name);
                            }}
                            className="text-violet-600 dark:text-violet-400 hover:text-violet-700 transition-colors"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {pdfError && <p className="text-xs text-destructive">{pdfError}</p>}
                <div className="flex gap-3 mt-3">
                  <Button variant="outline" onClick={handleReviewPdf} disabled={pdfBusy} className="flex-1">
                    {pdfBusy ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Preparing document…
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-2" />
                        Legal review PDF
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={handleReset} className="flex-1">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Run Again
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {phase === 'error' && (
          <div className="mt-6">
            <Card className="border-red-300/40 dark:border-red-500/30">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <XCircle className="h-5 w-5 text-red-500" />
                  Failed
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 space-y-2">
                  <p className="text-sm text-destructive break-words">{error || 'An error occurred during execution'}</p>
                  {errorDetails && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setErrorExpanded((v) => !v)}
                        className="flex items-center justify-between w-full text-xs font-medium text-destructive hover:underline"
                      >
                        <span>{errorExpanded ? 'Hide full error' : 'Show full error'}</span>
                        {errorExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </button>
                      {errorExpanded && (
                        <pre
                          className={
                            'mt-2 text-xs text-left text-destructive/90 bg-background/60 ' +
                            'border border-destructive/20 rounded-md p-3 overflow-auto max-h-80 ' +
                            'whitespace-pre-wrap break-words font-mono'
                          }
                        >
                          {errorDetails}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
                <Button variant="outline" onClick={handleReset} className="w-full">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {error && phase === 'input' && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}
      </div>

      {/* Multimedia viewer modal */}
      {viewerIndex !== null && outputs[viewerIndex] && (
        <OutputViewer
          output={outputs[viewerIndex]}
          showCopySightResult
          copysightLoading={copysightLoading}
          name={downloadNames[outputs[viewerIndex].filename] || outputs[viewerIndex].filename}
          index={viewerIndex}
          total={outputs.length}
          onClose={closeViewer}
          onPrev={showPrev}
          onNext={showNext}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
}
